import { useState, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'APP/Hooks/redux'
import ActiveMinutesActions from 'APP/Redux/ActiveMinutesRedux'
import moment from 'moment'
import { USER_TRACKER_STATUSES } from 'APP/Lib/Enums'

/**
 * This hook is used to check if the user has any tracker connected in the past 90sec
 * if so, it will expose the syncing state (true) that will be resolved (false) once 90sec pass from the last tracker connection
 */
export const useTrackerSyncingStatus = (userTrackers, onSyncFinished) => {
  const [isSyncing, setIsSyncing] = useState(false)
  const synchronizationDelayLimit = 90000

  const trackerConnectedDifference = useMemo(() => {
    if (!userTrackers) return 0
    const trackerConnectedDifferences = [
      ...userTrackers
        .filter(({ created_at }) => !!created_at)
        .map(({ created_at }) => {
          const difference = moment().diff(moment.utc(created_at))
          return difference < 0 ? 0 : difference
        }),
    ]
    return trackerConnectedDifferences
      .filter((delay) => delay < synchronizationDelayLimit)
      .sort((delay1, delay2) => delay1 - delay2)[0]
  }, [userTrackers])

  useEffect(() => {
    if (trackerConnectedDifference >= 0) {
      let timeoutId
      timeoutId = setTimeout(() => {
        onSyncFinished()
        setIsSyncing(false)
        timeoutId = undefined
      }, synchronizationDelayLimit - trackerConnectedDifference)

      setIsSyncing(() => !!timeoutId)

      return () => {
        clearTimeout(timeoutId)
        setIsSyncing(false)
        timeoutId = undefined
      }
    }
  }, [trackerConnectedDifference])

  return isSyncing
}

export const useConnectedTrackerStatus = ({ enabled, isNoData, onSyncFinished }) => {
  const dispatch = useDispatch()
  const getConnectedTrackers = () => dispatch(ActiveMinutesActions.getConnectedTrackers())

  const {
    data: trackers,
    loading,
    retry,
  } = useSelector((state) => state.activeMinutes.connectedTrackers)

  useEffect(() => {
    if (enabled) {
      getConnectedTrackers()
    }
  }, [enabled])

  const isSyncing = useTrackerSyncingStatus(trackers, onSyncFinished)

  const status = useMemo(() => {
    if (!enabled || !trackers || loading || retry) return undefined

    if (!trackers.length) return USER_TRACKER_STATUSES.NO_TRACKERS

    if (isSyncing) return USER_TRACKER_STATUSES.SYNCING

    if (
      isNoData &&
      trackers.find((tracker) => {
        if (!tracker.created_at) return false

        const lastCreatedAt = moment.utc(tracker.created_at)
        const isntMonday = new Date().getDay() !== 1
        const connectedSince24H = lastCreatedAt.isBefore(moment().subtract(1, 'days'))

        return isntMonday && connectedSince24H
      })
    )
      return USER_TRACKER_STATUSES.NO_DATA

    if (trackers.find((tracker) => tracker.status !== 'connected')) {
      return USER_TRACKER_STATUSES.BROKEN_TRACKER
    }
  }, [enabled, trackers, loading, retry, isSyncing, isNoData])

  return status
}
